import { useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";

import { caseImagesService } from "../../services/caseImages.service";
import { internalRenderService } from "../../services/internalRender.service";
import {
  isRenderableReportFile,
  type ReportFileAttachment,
} from "./ReportFilePage";
import type { ReportImageAttachment } from "./ReportImagePage";

type ResolvedUrl = { url: string };
type ResolvableImage = ReportImageAttachment & { imageUrl?: string };
type ResolvableFile = ReportFileAttachment & { url?: string };
type AttachmentMode = "internal" | "regular";
const INTERNAL_RENDER_RETRY_COUNT = 1;
const INTERNAL_RENDER_RETRY_DELAY_MS = 100;

interface ReportAttachmentUrlOptions {
  caseId: string;
  contentVersionKey?: string;
  images: ResolvableImage[];
  files: ResolvableFile[];
  enabled: boolean;
  mode: AttachmentMode;
}

interface AttachmentQueries<T> {
  caseId: string;
  contentVersionKey?: string;
  attachments: T[];
  enabled: boolean;
  mode: AttachmentMode;
}

function resolveRegularImage(image: ResolvableImage): Promise<ResolvedUrl> {
  if (!image.imageUrl)
    return Promise.reject(new Error("Report photo locator is unavailable."));
  return caseImagesService.getDownloadUrl(image.imageUrl, image.fileName);
}

function resolveRegularFile(file: ResolvableFile): Promise<ResolvedUrl> {
  if (!file.url)
    return Promise.reject(
      new Error("Report attachment locator is unavailable.")
    );
  return caseImagesService.getDownloadUrl(file.url, file.name);
}

function useImageQueries(options: AttachmentQueries<ResolvableImage>) {
  return useQueries({
    queries: options.attachments.map((image) => ({
      queryKey: [
        "report-image-url",
        options.caseId,
        image.id,
        options.mode,
        options.contentVersionKey,
      ],
      queryFn: () =>
        options.mode === "internal"
          ? internalRenderService.getSelectedImageUrl(image.id)
          : resolveRegularImage(image),
      enabled: options.enabled,
      retry: options.mode === "internal" ? INTERNAL_RENDER_RETRY_COUNT : false,
      retryDelay: INTERNAL_RENDER_RETRY_DELAY_MS,
    })),
  });
}

function useFileQueries(options: AttachmentQueries<ResolvableFile>) {
  return useQueries({
    queries: options.attachments.map((file) => ({
      queryKey: [
        "report-file-url",
        options.caseId,
        file.id,
        options.mode,
        isRenderableReportFile(file),
        options.contentVersionKey,
      ],
      queryFn: () =>
        options.mode === "internal"
          ? internalRenderService.getSelectedFileUrl(file.id)
          : resolveRegularFile(file),
      enabled:
        options.enabled &&
        (options.mode === "regular" || isRenderableReportFile(file)),
      retry: options.mode === "internal" ? INTERNAL_RENDER_RETRY_COUNT : false,
      retryDelay: INTERNAL_RENDER_RETRY_DELAY_MS,
    })),
  });
}

function resolvedUrls<T extends { id: string }>(
  attachments: T[],
  queries: UseQueryResult<ResolvedUrl>[]
): Map<string, string> {
  return new Map(
    attachments.flatMap((attachment, index) => {
      const url = queries[index]?.data?.url;
      return url ? [[attachment.id, url] as const] : [];
    })
  );
}

function failedIds<T extends { id: string }>(
  attachments: T[],
  queries: UseQueryResult<ResolvedUrl>[]
): Set<string> {
  return new Set(
    attachments
      .filter((_attachment, index) => queries[index]?.isError)
      .map(({ id }) => id)
  );
}

function useAttachmentQueryState<T extends { id: string }>(
  attachments: T[],
  queries: UseQueryResult<ResolvedUrl>[]
) {
  return {
    resolvedUrls: useMemo(
      () => resolvedUrls(attachments, queries),
      [attachments, queries]
    ),
    failedIds: useMemo(
      () => failedIds(attachments, queries),
      [attachments, queries]
    ),
  };
}

export function useReportAttachmentUrls(options: ReportAttachmentUrlOptions) {
  const shared = {
    caseId: options.caseId,
    contentVersionKey: options.contentVersionKey,
    enabled: options.enabled,
    mode: options.mode,
  };
  const imageQueries = useImageQueries({
    ...shared,
    attachments: options.images,
  });
  const fileQueries = useFileQueries({ ...shared, attachments: options.files });
  const images = useAttachmentQueryState(options.images, imageQueries);
  const files = useAttachmentQueryState(options.files, fileQueries);
  return {
    imageQueries,
    fileQueries,
    resolvedImageUrls: images.resolvedUrls,
    resolvedFileUrls: files.resolvedUrls,
    queryFailedImageIds: images.failedIds,
    failedFileIds: files.failedIds,
  };
}
